import React from 'react';
import { formatDistanceToNow } from 'date-fns';

interface JobCardProps {
  job: any;
  onAccept: () => void;
  onChat: () => void;
  loading?: boolean;
  distance?: number;
}

const JobCard: React.FC<JobCardProps> = ({ job, onAccept, onChat, loading = false, distance }) => {
  const formatCurrency = (amount: number) => `P${amount.toFixed(2)}`;
  const formatTimeAgo = (dateString: string) => formatDistanceToNow(new Date(dateString), { addSuffix: true });

  const getUrgencyColor = (createdAt: string) => {
    const hoursAgo = (Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60);
    if (hoursAgo < 1) return 'bg-green-100 text-green-800';
    if (hoursAgo < 2) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-200 hover:border-blue-300">
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-1">
            <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">{job.title}</h3>
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getUrgencyColor(job.created_at)}`}>
              {formatTimeAgo(job.created_at)}
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-2">{job.errand_types?.name} • {job.errand_types?.description}</p>
        </div>
        <div className="text-right ml-4">
          <div className="text-2xl font-bold text-green-600">{formatCurrency(job.driver_earnings || 0)}</div>
          <div className="text-xs text-gray-500">Your earnings</div>
        </div>
      </div>

      {/* Description */}
      {job.description && (
        <p className="text-gray-700 mb-4 line-clamp-2">{job.description}</p>
      )}

      {/* Locations */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="flex items-start space-x-2">
          <div className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">Pickup</p>
            <p className="text-sm text-gray-600">{job.pickup_location}</p>
          </div>
        </div>

        {job.dropoff_location && (
          <div className="flex items-start space-x-2">
            <div className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Drop-off</p>
              <p className="text-sm text-gray-600">{job.dropoff_location}</p>
            </div>
          </div>
        )}
      </div>

      {/* Job Details */}
      <div className="bg-gray-50 rounded-lg p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Total Pay</p>
            <p className="text-lg font-semibold text-gray-900">{formatCurrency(job.total_cost || 0)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Your Cut</p>
            <p className="text-lg font-semibold text-green-600">{formatCurrency(job.driver_earnings || 0)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Payment</p>
            <p className="text-sm font-medium text-blue-600">
              {job.payment_method === 'cash_on_delivery' ? '💰 Cash' : '💳 Wallet'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Distance</p>
            <p className="text-sm font-medium text-purple-600">
              {distance ? `${distance.toFixed(1)}km` : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Customer Info */}
      <div className="flex items-center justify-between mb-4 p-3 bg-blue-50 rounded-lg">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-blue-200 rounded-full flex items-center justify-center">
            <span className="text-sm font-medium text-blue-800">
              {(job.profiles?.full_name || 'Customer').charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">{job.profiles?.full_name || 'Customer'}</p>
            <p className="text-xs text-gray-600">⭐ 4.5 rating</p>
          </div>
        </div>
        <button
          onClick={onChat}
          className="px-3 py-1.5 text-blue-600 hover:text-blue-800 text-sm font-medium hover:bg-blue-100 rounded-lg transition-colors"
        >
          💬 Message
        </button>
      </div>

      {/* Actions */}
      <div className="flex space-x-3">
        <button
          onClick={onAccept}
          disabled={loading}
          className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Accepting...</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Accept Job</span>
            </>
          )}
        </button>

        <button
          onClick={onChat}
          className="px-4 py-3 border border-gray-300 hover:border-gray-400 text-gray-700 hover:text-gray-900 rounded-lg font-medium transition-colors"
        >
          💬
        </button>
      </div>

      {/* Urgency Indicator */}
      <div className="mt-3 text-center">
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-800">
          ⚡ High demand - Accept now!
        </span>
      </div>
    </div>
  );
};

export default JobCard;
